import {createContractorFormModal} from "../../admin/contractors/modal";
import {randomUniqueClassName} from "../../helpers/random";
import {waitUntil} from "../../helpers/waitUntil";
import {PickExistingContractorModal} from "../../admin/contractors/PickExistingContractor/PickExistingContractorModal";
import Axios from "axios";
import {handleAjaxSuccess} from "../../helpers/SwalHelper";
import handleAjaxError from "../../helpers/handleAjaxError";
import {hideLoading, showLoading} from "../../helpers/loading";


/**
 * @typedef {Object} SelectOptions
 * @property {(contractor: Object<string,any>) => void|undefined} [onContractorCreate]
 * @property {() => void} [onContractorModalOpen]
 * @property {() => void} [onContractorModalBack]
 * @property {boolean=false} withBackButtonForContractorModal
 */

/**
 * Initializes a contractors select, used to select a contractor from a list
 *
 * @param {Element} element
 * @param {SelectOptions} settings
 * @return {JQuery<HTMLElement>}
 */
function initContractorSelect(element, settings = {}) {
	const defaultSettings = {};

	/** @type {SelectOptions} */
	const finalSettings = {
		...defaultSettings,
		...settings,
	}

	const select2Settings = {
		theme: 'bootstrap4',
		templateResult: contractor => {
			if (!contractor.id || !contractor.element) {
				return contractor.text;
			}
			let tags = [];
			try {
				tags = JSON.parse(contractor.element.dataset.tags);
				if (tags === null) {
					tags = [];
				}
			} catch (e) {

			}

			let tagsString = tags.join(', ');

			if (tagsString.length === 0) {
				tagsString = 'No Trades';
			}

			return $(`
				<div style="line-height: 1">
					<div>${contractor.text}</div>
					<div>
						<small class="text-muted">
							${tagsString}
						</small>
					</div>
				</div>
			`);

		},
		matcher: (params, data) => {
			// If there are no search terms, return all of the data
			if (!params || !params.term || params.term.trim() === '') {
				return data;
			}

			// Skip if there is no 'children' property
			if (typeof data.children === 'undefined') {
				return null;
			}
			const searchTerm = params.term.toUpperCase();

			const modifiedData = $.extend({}, data, true);
			// Filter contractors
			// noinspection JSPrimitiveTypeWrapperUsage
			modifiedData.children = data.children.filter(contractor => {
				const stringIsPartOfText = contractor.text.toUpperCase().indexOf(searchTerm) > -1;

				let tags = [];
				try {
					tags = JSON.parse(contractor.element.dataset.tags);
					if (tags === null) {
						tags = [];
					}
				} catch (e) {

				}

				let stringIsPartOfTag = false;
				for (const tag of tags) {
					if (tag.toUpperCase().indexOf(searchTerm) > -1) {
						stringIsPartOfTag = true;
						break;
					}
				}

				return stringIsPartOfText || stringIsPartOfTag;
			})

			// Return `null` if the term should not be displayed
			return modifiedData;
		}
	}

	// We may need to set the dropdown parent if we are inside a modal
	const dropdownParent = $(element).closest('.modal-content')[0];

	if (dropdownParent) {
		select2Settings.dropdownParent = dropdownParent;
	}

	const select = element.querySelector('.contractor-select-component_select');
	const select2Element = $(select).select2(select2Settings);

	// We need to add w-100 to the select2 container because it may be initialized off screen
	select2Element.data('select2').$container.addClass('w-100');

	// Init add button if possible
	const addContractorButton = element.querySelector('.contractor-select-component_add-contractor-button');
	if (addContractorButton) {
		// Requires modal 'admin.contractors.modal' to be defined
		const createContractorModal = createContractorFormModal({
			onFormSubmit: contractor => {
				addNewContractorToSelect(contractor);

				// Notify parents of creation
				if (finalSettings.onContractorCreate) {
					finalSettings.onContractorCreate(contractor);
				}

				// Hide contractor modal
				createContractorModal.hide();

				// Call back method if required
				if (finalSettings.onContractorModalBack) {
					finalSettings.onContractorModalBack()
				}
			},
			withBackButton: finalSettings.withBackButtonForContractorModal,
			onBackAction: finalSettings.onContractorModalBack,
		});

		addContractorButton.addEventListener('click', () => {
			const swalButtonsContainerClass = randomUniqueClassName();

			// Switch between create new contractor or pick existing
			Swal.fire({
				title: 'Add Contractor',
				html: `
					<div class="d-flex flex-column align-items-stretch ${swalButtonsContainerClass}">
						<button class="btn btn-primary new-contractor-button">
							ADD NEW<br>
							<span class="small">Create a new contractor from scratch</span>
						</button>
						<button class="btn btn-info mt-2 existing-contractor-button">
							PICK EXISTING<br>
							<span class="small">Add existing contractor to project</span>
						</button>
					</div>
				`,
				showCancelButton: false,
				showConfirmButton: false,
			}).then(async result => {
				if (result.isConfirmed) {
					// We treat confirmed swal popup as create new
					if (finalSettings.onContractorModalOpen) {
						finalSettings.onContractorModalOpen();
					}

					createContractorModal.display(true);
				} else if (result.isDenied) {
					if (finalSettings.onContractorModalOpen) {
						finalSettings.onContractorModalOpen();
					}

					// noinspection JSUnresolvedReference
					const getExistingContractorModalRoute = element.dataset.getExistingContractorModalRoute;
					// noinspection JSUnresolvedReference
					const assignContractorToProjectRoute = element.dataset.assignContractorToProjectRoute;


					// We treat denied swal popup as pick existing
					const pickContractorModal = await new PickExistingContractorModal(
						getExistingContractorModalRoute,
						{
							onSuccess() {
								const contractor = pickContractorModal.getSelectedContractor();

								// Assign contractor to project
								showLoading();

								Axios.post(
									assignContractorToProjectRoute,
									{
										contractor_id: contractor.id,
										// project_id comes defined with the route
									}
								)
									.then(response => {
										handleAjaxSuccess(response, () => {
											addNewContractorToSelect(contractor);

											// Notify parents of creation
											if (finalSettings.onContractorCreate) {
												finalSettings.onContractorCreate(contractor);
											}

											// Hide contractor modal
											pickContractorModal.hide();

											// Call back method if required
											if (finalSettings.onContractorModalBack) {
												finalSettings.onContractorModalBack()
											}
										})
									})
									.catch(handleAjaxError)
									.finally(hideLoading)
							},
							dataTableConfig: {},
						}
					);
				}
			})

			// Initialize buttons
			waitUntil(() => document.querySelector(`.${swalButtonsContainerClass}`))
				.then(() => {
					const buttonsContainer = document.querySelector(`.${swalButtonsContainerClass}`);

					const newContractorButton = buttonsContainer.querySelector('.new-contractor-button');
					const existingContractorButton = buttonsContainer.querySelector('.existing-contractor-button');

					// Reuse confirm as create new
					newContractorButton.addEventListener('click', () => {
						Swal.clickConfirm();
					})

					// Reuse deny as pick existing
					existingContractorButton.addEventListener('click', () => {
						Swal.clickDeny();
					})
				})
		});
	}

	return select2Element;

	function addNewContractorToSelect(contractor) {
		// Add contractor to select after creation
		const contractorFullName = `${contractor.first_name} ${contractor.last_name}`.trim();

		let optionDisplayName
		if (contractorFullName) {
			optionDisplayName = `${contractor.company_name} - ${contractorFullName}`.trim();
		} else {
			optionDisplayName = `${contractor.company_name}`.trim();
		}
		// noinspection JSUnresolvedVariable

		// Add to select
		const newOption = new Option(optionDisplayName, contractor.id, false, true);

		//// Add trades string
		// noinspection JSUnresolvedReference
		newOption.dataset.tags = JSON.stringify(contractor.trades);

		// Append to DOM
		$(select).append(newOption);

		// Select new option
		$(select).val(contractor.id)

		// Trigger changes
		$(select).trigger('change');
		$(select2Element).trigger('change');
	}
}

export {initContractorSelect}
